import { Component, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  KycRejectedImage,
  KycRejectionHeader,
  Rejection,
} from 'src/app/_modules/customers/_models/kyc-rejection';
import {
  Address,
  KycAccountDetails,
  KycHeader,
  getInitHeader,
} from 'src/app/models/kyc.model';
import { PermissionsEnum } from 'src/app/models/role.model';
import { OptionObj } from 'src/app/models/util.model';
import { AccountKycService } from 'src/app/services/kyc/account-kyc.service';
import {
  AuthService,
  GeographicalRegion,
  GeographicalRegionService,
  PermissionsService,
  SubGeographicalRegion,
  SubGeographicalRegionService,
} from 'shared-lib';
import { TranslationService } from 'src/app/services/translation.service';
import { PersonalDataStateService } from 'src/app/_modules/customers/components/personal-data/services/personal-data-state.service';
import { NavigationService } from 'src/app/services/shared/navigation.service';
import { RegionService } from 'src/app/services/region.service';
import { AreaService } from 'src/app/services/area.service';
import { Area } from 'src/app/models/area.model';

@Component({
  selector: 'app-new-service-details',
  templateUrl: './new-service-details.component.html',
  styleUrls: ['./new-service-details.component.scss'],
})
export class NewServiceDetailsComponent implements OnInit, OnDestroy {
  PermissionsEnum = PermissionsEnum;
  kycHeaderInfo: KycHeader = getInitHeader();

  accountInfo: KycAccountDetails;
  clientType: string = '';
  customerType: string = '';
  editEnabled: boolean = false;
  onlyView: boolean = false;
  customerId = 0;

  get isExistingCustomer(): boolean {
    return this.accountInfo?.customerId != null;
  }
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  selectedImage: string = '';
  openApproveModal: boolean = false;
  openRejectModal: boolean = false;
  imgElement: any;
  imgZoomFullContainer: any;
  currentRotation: number = 0;
  showApprove: boolean = false;
  showReject: boolean = false;
  showSuspend: boolean = false;
  showRecallMenu: boolean = false;
  informationMissing: boolean = false;

  customerDataMissing: boolean = false;
  customerDataFields: string[] = [];
  customerDataRejectionSelected: string[] = [];
  customerDataRejectionOther: string = '';
  servicePointDataMissing: boolean = false;
  servicePointDataFields: string[] = [];
  servicePointDataRejectionSelected: string[] = [];
  servicePointDataRejectionOther: string = '';
  allAreas: Area[] = [];

  filesMissingFields: KycRejectedImage[] = [];
  filesMissing: boolean = false;
  filesRejectionSelected: string[] = [];
  filesRejectionOther: string = '';

  meterRequestRejectionReason: string = '';

  dtName: string = this.translationService.getByKeyFromStorage('DTNumber');
  totalLoad?: number = 0;
  isGeolocationMapVisible: boolean = false;
  selectedMapLatitude: number | null = null;
  selectedMapLongitude: number | null = null;

  readonly OTHER_OPTION = 'Other inconsistencies or non-compliance with application requirements';

  readonly CUSTOMER_DATA_REJECTION_OPTIONS = [
    'Name on the application does not correspond with the name on the means of identification',
    'Expired means of identification (International Passport or Driver\'s License)',
    'Other inconsistencies or non-compliance with application requirements',
  ];

  readonly SERVICE_POINT_REJECTION_OPTIONS = [
    'Incomplete address or incorrect zone selection',
    'Address on the proof of ownership differs from the applicant\'s address',
    'Other inconsistencies or non-compliance with application requirements',
  ];

  readonly FILES_REJECTION_OPTIONS = [
    'Name on the application does not correspond with the name on the means of identification',
    'Blurred or unclear passport photograph',
    'Expired licensed electrical contractor certificate',
    'Address on the proof of ownership differs from the applicant\'s address',
    'No proof of ownership attached',
    'Consent letter from the property owner not provided (where applicable)',
    'Uploaded documents are unclear or blurred',
    'Incomplete or unreadable supporting documents',
    'Other inconsistencies or non-compliance with application requirements',
  ];
  KycRejectedImage = KycRejectedImage;
  onboardedCustomersDetails: boolean = false;

  regionOptions: OptionObj[] = [];
  areaOptions: OptionObj[] = [];
  selectedRegionId: number = 0;
  selectedAreaId: number = 0;

  geoRegions: GeographicalRegion[] = [];
  subGeoRegions: SubGeographicalRegion[] = [];
  selectableSubGeoRegions: SubGeographicalRegion[] = [];
  selectedGeoRegionId: number = 0;

  constructor(
    public permissionsService: PermissionsService,
    private activatedRoute: ActivatedRoute,
    private accountKycService: AccountKycService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private translationService: TranslationService,
    private el: ElementRef,
    private renderer: Renderer2,
    private stateService: PersonalDataStateService,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
    private regionService: RegionService,
    private areaService: AreaService,
    public authService: AuthService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService
  ) { }

  ngOnInit(): void {
    this.onboardedCustomersDetails = this.route.snapshot.pathFromRoot
      .flatMap(r => r.url)
      .some(segment => segment.path === 'onboarded-customer-details');
    const id = this.route.parent?.snapshot.params['id'];
    this.navigationService.setGoBackTo(`/new-service/${id}`);
    const customerId = this.activatedRoute.snapshot.paramMap.get('detailsId');
    this.customerId = Number(customerId);
    this.imgElement = this.el?.nativeElement?.querySelector(
      '.ngxImageZoomThumbnail',
    );
    this.imgZoomFullContainer =
      this.el?.nativeElement?.querySelector('.ngxImageZoomFull');
    this.getKycAccount(customerId);
  }

  ngOnDestroy(): void {
    this.navigationService.setGoBackTo(null);
  }

  edit() {
    this.editEnabled = true;
    if (!this.accountInfo.addressOfLandlord) {
      this.accountInfo.addressOfLandlord = new Address();
    }
    if (this.authService.hasCimDataIntegration) {
      this.loadGeoRegions();
      this.loadSubGeoRegions();
    } else {
      this.loadRegions();
    }
  }

  loadGeoRegions() {
    this.geographicalRegionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geoRegions = responseData.data ?? [];
        }
      },
      error: (_: any) => {
        this.toastr.error('Error loading regions.');
      },
    });
  }

  loadSubGeoRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.subGeoRegions = responseData.data ?? [];
          const current = this.subGeoRegions.find(
            (x) => x.id === this.accountInfo?.addressOfLandlord?.subGeographicalRegionId
          );
          this.selectedGeoRegionId = current?.geographicalRegion?.id ?? 0;
          this.selectableSubGeoRegions = this.selectedGeoRegionId
            ? this.subGeoRegions.filter((x) => x.geographicalRegion?.id === this.selectedGeoRegionId)
            : [];
        }
      },
      error: (_: any) => {
        this.toastr.error('Error loading areas.');
      },
    });
  }

  onGeoRegionChange(geoRegionId: number) {
    this.selectableSubGeoRegions = this.subGeoRegions.filter(
      (x) => x.geographicalRegion?.id === geoRegionId
    );
    this.accountInfo.addressOfLandlord.subGeographicalRegionId = null;
    this.accountInfo.addressOfLandlord.area = undefined;
    this.accountInfo.addressOfLandlord.region = this.geoRegions.find(
      (x) => x.id === geoRegionId
    )?.name;
  }

  onSubGeoRegionChange(subGeoRegionId: number) {
    this.accountInfo.addressOfLandlord.area = this.subGeoRegions.find(
      (x) => x.id === subGeoRegionId
    )?.name;
  }

  loadRegions() {
    this.regionService.getRegionsForOptions().subscribe({
      next: (response: any) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.regionOptions = response.data;
          const currentRegion = this.regionOptions.find(
            (r) => r.value === this.accountInfo?.addressOfLandlord?.region
          );
          if (currentRegion) {
            this.selectedRegionId = currentRegion.key;
            this.loadAreasByRegion(this.selectedRegionId);
          }
        }
      },
      error: (_: any) => {
        this.toastr.error('Error loading regions.');
      },
    });
  }

  loadAreasByRegion(regionId: number) {
    this.areaService.getAreasForOptionsByRegion(regionId).subscribe({
      next: (response: any) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.areaOptions = response.data;
          const currentArea = this.areaOptions.find(
            (a) => a.key === this.accountInfo?.addressOfLandlord?.areaId
          );
          if (currentArea) {
            this.selectedAreaId = currentArea.key;
          } else {
            this.selectedAreaId = 0;
          }
        }
      },
      error: (_: any) => {
        this.toastr.error('Error loading areas.');
      },
    });
  }

  onRegionChange(regionId: number) {
    this.selectedAreaId = 0;
    this.areaOptions = [];
    this.accountInfo.addressOfLandlord.areaId = undefined;
    this.accountInfo.addressOfLandlord.area = undefined;
    const selectedRegion = this.regionOptions.find(
      (r) => r.key === regionId
    );
    if (selectedRegion) {
      this.accountInfo.addressOfLandlord.region = selectedRegion.value;
    }
    if (regionId) {
      this.loadAreasByRegion(regionId);
    }
  }

  onAreaChange(areaId: number) {
    const selectedArea = this.areaOptions.find(
      (a) => a.key === areaId
    );
    if (selectedArea) {
      this.accountInfo.addressOfLandlord.areaId = selectedArea.key;
      this.accountInfo.addressOfLandlord.area = selectedArea.value;
    }
  }

  openEditModal() { }

  getMobileUsers() { }

  openGeolocationMap(latitude?: number | null, longitude?: number | null) {
    if (latitude == null || longitude == null) {
      return;
    }

    this.selectedMapLatitude = latitude;
    this.selectedMapLongitude = longitude;
  }

  onGeolocationMapModalOpen() {
    this.isGeolocationMapVisible = true;
  }

  onGeolocationMapModalFade() {
    this.isGeolocationMapVisible = false;
    this.selectedMapLatitude = null;
    this.selectedMapLongitude = null;
  }

  rotateLeft() {
    this.currentRotation -= 90;
  }
   openPdf(pdfUrl : string) {
    if(pdfUrl == '')
    {
      this.toastr.info("No PDF to view.")
      return;
    } 
    window.open(pdfUrl, '_blank');
  }
  cancelModal() {
    this.showApprove = false;
    this.showReject = false;
    this.showSuspend = false;
  }
  rotateRight() {
    this.currentRotation += 90;
  }
  approve(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showApprove = true;
    this.showReject = false;
  }

  reject(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showApprove = false;
    this.showReject = true;
    this.showSuspend = false;
  }

  suspend(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showSuspend = true;
    this.showApprove = false;
    this.showReject = false;
  }

  showImage(num: number) {
    if (num === 0) {
      this.selectedImage = this.getFrontViewImage(0) || '';
    } else if (num === 1) {
      this.selectedImage = this.getFrontViewImage(1) || '';
    } else if (num === 2) {
      this.selectedImage = this.getFrontViewImage(2) || '';
    } else if (num === 3) {
      this.selectedImage = this.getBuildingMountedImage() || '';
    } else if (num === 4) {
      this.selectedImage =
        this.accountInfo.customerInformation?.cardImageUrl || '';
    } else if (num === 5) {
      this.selectedImage = this.accountInfo.customersSignitureUrl;
    } else if (num === 6) {
      this.selectedImage = this.accountInfo.landlordsSignitureUrl;
    } else if (num === 7) {
      this.selectedImage = this.accountInfo.electricalContractorsSignitureUrl;
    } else if (num === 8) {
      this.selectedImage = this.accountInfo.applicantsSignitureUrl;
    } else if (num === 9) {
      this.selectedImage = this.accountInfo.passportUrl;
    }
  }

  getBuildingMountedImage() {
    return this.accountInfo?.customerInformation?.buildingImages?.find(
      (x) => x.imageType === 'MountedTag',
    )?.url;
  }

  getFrontViewImage(id: number) {
    return this.accountInfo?.customerInformation?.buildingImages?.filter(
      (x) => x.imageType === 'Frontview',
    )[id]?.url;
  }

  isVisibleField(field: string) {
    var notVisibleFields = localStorage.getItem('notVisibleFields');
    return notVisibleFields?.includes(field) ? false : true;
  }

  onModalFade() {
    this.currentRotation = 0;

    if (this.imgZoomFullContainer) {
      var displayValue = getComputedStyle(this.imgZoomFullContainer).display;

      if (displayValue == 'block') {
        this.imgElement.click();
      }
    }
  }

  onModalOpen() {
    this.renderer.setStyle(document.body, 'overflow', 'auto');
    this.renderer.setStyle(document.body, 'overflow-x', 'hidden');
    this.renderer.setStyle(document.body, 'padding-right', '0');
  }

  private getKycAccount(kycId: any) {
    this.accountKycService.getKycAccountById(kycId).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.accountInfo = response.data;
          if (this.accountInfo.customerInformation) {
            this.accountInfo.customerInformation.meterNumber = response.data?.customerInformation?.meter?.number ?? '';
          }
          this.populateRegionAndArea();
          this.populateHeaderInfo();
          this.setClientAndCustomerType();
        } else {
          this.toastr.error(response?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  populateRegionAndArea(){
    this.areaService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          if (this.accountInfo.customerInformation) {
            this.accountInfo.customerInformation.area = this.allAreas.find(x => x.id == this.accountInfo.customerInformation.areaId) ?? null;
          }
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  setClientAndCustomerType() {
    this.clientType = this.stateService.setClientType(this.accountInfo.clientType) ?? '';
    this.customerType = this.stateService.setCustomerType(this.accountInfo.customerType) ?? '';
  }

  private populateHeaderInfo() {
    this.kycHeaderInfo.firstName = this.accountInfo.firstName ?? '';
    this.kycHeaderInfo.surname = this.accountInfo.surname;
    this.kycHeaderInfo.slrn =
      this.accountInfo.customerInformation?.building?.slrn;
    this.kycHeaderInfo.address =
      'No.' +
      this.accountInfo.employerAddress?.houseNo +
      ' ' +
      this.accountInfo.employerAddress?.street +
      ', ' +
      this.accountInfo.employerAddress?.landmark;
    this.kycHeaderInfo.phoneNumber =
      '+' +
      this.accountInfo.dialCode +
      (this.accountInfo.phoneNumber?.replace(/\s/g, '') ?? '');
    this.kycHeaderInfo.email = this.accountInfo.email;
    this.kycHeaderInfo.applicationNumber = this.accountInfo.applicationNumber;

    this.totalLoad = this.accountInfo.loadRequirements?.reduce(
      (sum, lr) => sum + (lr.wattage ?? 0),
      0,
    );
  }

  cancel(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showApprove = false;
    this.showReject = false;
    this.showSuspend = false;
  }
  submitApproval() {
    this.accountKycService.approveKycAccount(this.accountInfo.id).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success('Successfully approved customer.');
          this.cancelModal();
          window.location.reload();
        } else {
        }
      },
      error: (_) => {
        this.toastr.error('Error occured.');
      },
    });
  }

  submitSuspend() {
    this.accountKycService.suspendKycAccount(this.accountInfo.id).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success('Successfully suspended customer.');
          this.cancelModal();
          window.location.reload();
        } else {
          this.toastr.error(response?.message || 'Failed to suspend.');
        }
      },
      error: (_) => {
        this.toastr.error('Error occured.');
      },
    });
  }

  recallValidation() {
    this.showRecallMenu = false;
    this.accountKycService.recallKycAccount(this.accountInfo.id).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success('Successfully recalled customer.');
          window.location.reload();
        } else {
          this.toastr.error(response?.message || 'Failed to recall.');
        }
      },
      error: (_) => {
        this.toastr.error('Error occured.');
      },
    });
  }

  trySubmitRejection() {
    if (this.validateRejection()) {
      this.populateRejections();

      this.accountKycService
        .saveKycRejection({
          accountId: this.accountInfo.id,
          rejections: this.accountInfo.kycRejections,
        })
        .subscribe({
          next: (response) => {
            if (response?.status === 200 || response?.status === 'OK') {
              this.toastr.success('Successfully rejected customer.');
              this.cancelModal();
              window.location.reload();
            } else {
            }
          },
          error: (_) => {
            this.toastr.error('Error occured.');
          },
        });
    }
  }

  onSelectChange(value: string, selectType: string) {
    if (selectType === 'missingInfo') {
      this.informationMissing = value === 'Yes';
      if (!this.informationMissing) {
        this.customerDataMissing = false;
        this.customerDataFields = [];
        this.customerDataRejectionSelected = [];
        this.customerDataRejectionOther = '';
        this.servicePointDataMissing = false;
        this.servicePointDataFields = [];
        this.servicePointDataRejectionSelected = [];
        this.servicePointDataRejectionOther = '';
        this.filesMissingFields = [];
        this.filesRejectionSelected = [];
        this.filesRejectionOther = '';
        this.filesMissing = false;
        this.meterRequestRejectionReason = '';
      }
    } else if (selectType === 'customerData') {
      this.customerDataMissing = value === 'Yes';
      if (!this.customerDataMissing) {
        this.customerDataFields = [];
        this.customerDataRejectionSelected = [];
        this.customerDataRejectionOther = '';
      }
    } else if (selectType === 'servicePointData') {
      this.servicePointDataMissing = value === 'Yes';
      if (!this.servicePointDataMissing) {
        this.servicePointDataFields = [];
        this.servicePointDataRejectionSelected = [];
        this.servicePointDataRejectionOther = '';
      }
    } else if (selectType === 'filesMissing') {
      this.filesMissing = value === 'Yes';
      if (!this.filesMissing) {
        this.filesMissingFields = [];
        this.filesRejectionSelected = [];
        this.filesRejectionOther = '';
      }
    }
  }

  toggleCustomerDataField(field: string) {
    const idx = this.customerDataFields.indexOf(field);
    if (idx === -1) {
      this.customerDataFields.push(field);
    } else {
      this.customerDataFields.splice(idx, 1);
    }
  }

  isCustomerDataFieldSelected(field: string): boolean {
    return this.customerDataFields.includes(field);
  }

  toggleServicePointField(field: string) {
    const idx = this.servicePointDataFields.indexOf(field);
    if (idx === -1) {
      this.servicePointDataFields.push(field);
    } else {
      this.servicePointDataFields.splice(idx, 1);
    }
  }

  isServicePointFieldSelected(field: string): boolean {
    return this.servicePointDataFields.includes(field);
  }

  toggleFilesField(field: KycRejectedImage) {
    const idx = this.filesMissingFields.indexOf(field);
    if (idx === -1) {
      this.filesMissingFields.push(field);
    } else {
      this.filesMissingFields.splice(idx, 1);
    }
  }

  isFilesFieldSelected(field: KycRejectedImage): boolean {
    return this.filesMissingFields.includes(field);
  }

  toggleRejectionOption(section: 'customerData' | 'servicePoint' | 'files', option: string) {
    const arr = this.getRejectionSelectedArray(section);
    const idx = arr.indexOf(option);
    if (idx === -1) arr.push(option);
    else arr.splice(idx, 1);
  }

  isRejectionOptionSelected(section: 'customerData' | 'servicePoint' | 'files', option: string): boolean {
    return this.getRejectionSelectedArray(section).includes(option);
  }

  isOtherSelected(section: 'customerData' | 'servicePoint' | 'files'): boolean {
    return this.getRejectionSelectedArray(section).includes(this.OTHER_OPTION);
  }

  private getRejectionSelectedArray(section: 'customerData' | 'servicePoint' | 'files'): string[] {
    if (section === 'customerData') return this.customerDataRejectionSelected;
    if (section === 'servicePoint') return this.servicePointDataRejectionSelected;
    return this.filesRejectionSelected;
  }

  private buildRejectionReason(selected: string[], otherComment: string): string {
    return selected
      .map(opt => opt === this.OTHER_OPTION && otherComment ? `Other: ${otherComment}` : opt)
      .join('; ');
  }

  populateRejections() {
    this.accountInfo.kycRejections = [];
    if (this.informationMissing) {
      if (this.customerDataMissing && this.customerDataFields.length) {
        const rejection = {
          rejectedPage: KycRejectionHeader.CustomerInformation,
          reason: this.buildRejectionReason(this.customerDataRejectionSelected, this.customerDataRejectionOther),
          stepNumber: 2,
        };
        const r = new Rejection(rejection);
        r.rejectedFields = [...this.customerDataFields];
        this.accountInfo.kycRejections.push(r);
      }

      if (this.servicePointDataMissing && this.servicePointDataFields.length) {
        const rejection = {
          rejectedPage: KycRejectionHeader.CustomerInformation,
          reason: this.buildRejectionReason(this.servicePointDataRejectionSelected, this.servicePointDataRejectionOther),
          stepNumber: 2,
        };
        const r = new Rejection(rejection);
        r.rejectedFields = [...this.servicePointDataFields];
        this.accountInfo.kycRejections.push(r);
      }

      if (this.filesMissing && this.filesMissingFields.length) {
        const rejection = {
          rejectedPage: KycRejectionHeader.CustomerInformation,
          reason: this.buildRejectionReason(this.filesRejectionSelected, this.filesRejectionOther),
          stepNumber: 8,
        };

        const r = new Rejection(rejection);
        r.rejectedImages = [...this.filesMissingFields];

        this.accountInfo.kycRejections.push(r);
      }

      if (this.meterRequestRejectionReason.trim()) {
        const rejection = {
          rejectedPage: KycRejectionHeader.MeterRequest,
          reason: this.meterRequestRejectionReason,
          stepNumber: 5,
        };
        this.accountInfo.kycRejections.push(new Rejection(rejection));
      }
    }
  }

  save() {
    const landlordAddress = this.accountInfo.addressOfLandlord;
    if (this.authService.hasCimDataIntegration ? !landlordAddress?.subGeographicalRegionId : !landlordAddress?.areaId) {
      this.toastr.warning(this.translateService.instant('SITE_VERIFICATION.VALIDATION.UT_REQUIRED'));
      return;
    }

    this.accountKycService.editKycAccount(this.accountInfo).subscribe({
      next: (response) => {
        if (response?.status === 200 || response?.status === 'OK') {
          this.toastr.success('Successfully updated account details.');
          window.location.reload();
        } else {
        }
      },
      error: (_) => {
        this.toastr.error('Error occured.');
      },
    });
    this.editEnabled = false;
  }
  public hideWord(w: any) {
    if (w) {
      if (w.length < 3) return w;
      return w.substring(0, 3) + '*'.repeat(w.length - 3);
    }
  }
  
  validateRejection() {
    const hasInfoIssue =
      this.informationMissing &&
      (this.customerDataMissing ||
        this.servicePointDataMissing ||
        this.filesMissing ||
        !!this.meterRequestRejectionReason.trim());

    if (!hasInfoIssue) {
      this.toastr.error(
        'You have to specify why you are rejecting this account.',
      );
      return false;
    }

    if (this.informationMissing) {
      if (
        !this.customerDataMissing &&
        !this.servicePointDataMissing &&
        !this.filesMissing &&
        !this.meterRequestRejectionReason.trim()
      ) {
        this.toastr.error(
          'You have to select at least one section with incomplete information.',
        );
        return false;
      }

      if (this.customerDataMissing) {
        if (!this.customerDataFields.length) {
          this.toastr.error(
            'You have to select at least one Customer Data field.',
          );
          return false;
        }
        if (!this.customerDataRejectionSelected.length) {
          this.toastr.error(
            'You have to specify the reason for rejecting the Customer Data.',
          );
          return false;
        }
        if (this.isOtherSelected('customerData') && !this.customerDataRejectionOther.trim()) {
          this.toastr.error('Please provide a comment for the "Other" option in Customer Data.');
          return false;
        }
      }

      if (this.servicePointDataMissing) {
        if (!this.servicePointDataFields.length) {
          this.toastr.error(
            'You have to select at least one Service Point Data field.',
          );
          return false;
        }
        if (!this.servicePointDataRejectionSelected.length) {
          this.toastr.error(
            'You have to specify the reason for rejecting the Service Point Data.',
          );
          return false;
        }
        if (this.isOtherSelected('servicePoint') && !this.servicePointDataRejectionOther.trim()) {
          this.toastr.error('Please provide a comment for the "Other" option in Service Point Data.');
          return false;
        }
      }

      if (this.filesMissing) {
        if (!this.filesMissingFields.length) {
          this.toastr.error(
            'You have to select at least one file that needs to be re-uploaded.',
          );
          return false;
        }
        if (!this.filesRejectionSelected.length) {
          this.toastr.error(
            'You have to specify the reason for rejecting the files.',
          );
          return false;
        }
        if (this.isOtherSelected('files') && !this.filesRejectionOther.trim()) {
          this.toastr.error('Please provide a comment for the "Other" option in Files.');
          return false;
        }
      }
    }

    return true;
  }
  subServieSpaces(value: string | undefined) {
    switch (value) {
      case 'PublicLoad':
        return 'Public Load';

      case 'MeterReplacement':
        return 'Meter Replacement';

      case 'Migration':
        return 'Migration';

      case 'Separation':
        return 'Separation';

      case 'PointLoadTransformer':
        return 'Point Load Transformer';

      case 'DedicatedFeeder':
        return 'Dedicated Feeder';

      default:
        return value;
    }
  }
}
